import { type ISecondaryCategoryRepository } from '@webapp/core/business-logic/interfaces/ISecondaryCategoryRepository'
import { type GetSecondaryCategoryByIdResponse } from '@webapp/core/data-access/repositories/SecondaryCategoryRepository/types/GetSecondaryCategoryByIdResponse'
import { type RequestTypeEnum } from '@webapp/core/entities/internal/enums/RequestTypeEnum'
import { type SecondaryCategoryConstructor } from '@webapp/core/entities/internal/SecondaryCategory'
import { formatRepoFiltersToApiFilters } from '@webapp/core/utils/repositories/formatRepoFiltersToApiFilters'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type RepoFilterParams } from '@webapp/infrastructure/interfaces/repositories/RepoFilterParams'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import { type RepoStandardResponse } from '@webapp/infrastructure/interfaces/repositories/RepoStandardResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { type GetSecondaryCategoryListResponse } from './types'
import { mapDepartment } from '../../mappers/mapDepartment'

interface Dependency {
  httpClient: IHttpClient
}

export class SecondaryCategoryRepository
  implements ISecondaryCategoryRepository
{
  constructor(private readonly deps: Dependency) {}

  async list(
    params: {
      userId?: number
      primaryCategoryId?: number
      requestType?: RequestTypeEnum
    } & RepoFilterParams = {}
  ): Promise<RepoPaginatedResponse<SecondaryCategoryConstructor>> {
    const { userId, primaryCategoryId, requestType, ...filterParams } = params

    try {
      const res =
        await this.deps.httpClient.get<GetSecondaryCategoryListResponse>(
          `/api/v3/departments/`,
          {
            params: {
              requestable_user: userId,
              branch: primaryCategoryId,
              requestable: requestType,
              ...formatRepoFiltersToApiFilters(filterParams),
            },
          }
        )

      const secondaryCategories = res.data.data
      const metadata = res.data.metadata

      return {
        data: secondaryCategories.map((department) =>
          mapDepartment(department)
        ),
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getById(
    secondaryCategoryId: number,
    params: {
      requestType?: RequestTypeEnum
    } & RepoFilterParams = {}
  ): Promise<RepoStandardResponse<SecondaryCategoryConstructor> | undefined> {
    const { requestType, ...filterParams } = params

    try {
      const res =
        await this.deps.httpClient.get<GetSecondaryCategoryByIdResponse>(
          `/api/v3/departments/${secondaryCategoryId}/`,
          {
            params: {
              requestable: requestType,
              ...formatRepoFiltersToApiFilters(filterParams),
            },
          }
        )

      return { data: mapDepartment(res.data.data) }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }
}
